/**
 * The recent label jobs, newest first, with a Cancel on anything still queued.
 *
 * A list rather than a DataTable: five facts per row, read at arm's length on a phone or a
 * laptop beside the printer, and it should look the same on both.
 */
import type { UseQueryResult } from '@tanstack/react-query';
import { AgeText, Button, EmptyState, ErrorState, Pill, Skeleton } from '@/components';
import { ApiProblem } from '@/lib/api';
import { STATUS_LABEL, STATUS_TONE, toIsoInstant } from './labels';
import { useCancelVialJob } from './queries';
import type { VialJob, VialJobsResponse } from './types';

export function JobList({ jobs }: { jobs: UseQueryResult<VialJobsResponse> }) {
  const cancel = useCancelVialJob();

  if (jobs.isLoading) return <Skeleton shape="row" lines={3} />;
  if (jobs.isError) {
    return (
      <ErrorState
        message="The job list did not load."
        requestId={jobs.error instanceof ApiProblem ? jobs.error.shortRequestId : null}
        onRetry={() => void jobs.refetch()}
      />
    );
  }

  const rows = jobs.data?.jobs ?? [];
  if (rows.length === 0) return <EmptyState message="No label jobs yet." />;

  const cancelProblem =
    cancel.error instanceof ApiProblem
      ? cancel.error.message
      : cancel.error instanceof Error
        ? cancel.error.message
        : null;

  return (
    <div className="bench-jobs">
      <h3 className="bench-jobs__title">Recent jobs</h3>
      {cancelProblem ? (
        <p className="bench-problem" role="alert">
          {cancelProblem}
        </p>
      ) : null}
      <ul className="bench-jobs__list" role="list" aria-label="Label jobs">
        {rows.map((job) => (
          <JobRow
            key={job.id}
            job={job}
            busy={cancel.isPending && cancel.variables === job.id}
            onCancel={() => cancel.mutate(job.id)}
          />
        ))}
      </ul>
    </div>
  );
}

function JobRow({ job, busy, onCancel }: { job: VialJob; busy: boolean; onCancel: () => void }) {
  const count =
    job.status === 'done' && job.printed_qty !== job.qty
      ? `${job.printed_qty} of ${job.qty}`
      : `${job.qty}`;
  return (
    <li className="bench-job" role="listitem">
      <div className="bench-job__what">
        <span className="bench-job__name">{job.label_name}</span>
        <span className="o-muted"> {job.content}</span>
        <div className="o-mono o-muted bench-job__lot">lot {job.batch}</div>
      </div>
      <div className="bench-job__qty" aria-label="Quantity">
        {count}
      </div>
      <div className="bench-job__status">
        <Pill tone={STATUS_TONE[job.status]}>{STATUS_LABEL[job.status]}</Pill>
        {job.agent_note ? <div className="o-muted bench-job__note">{job.agent_note}</div> : null}
      </div>
      <div className="bench-job__meta o-muted">
        {job.requested_by}, <AgeText at={toIsoInstant(job.created_at)} />
      </div>
      <div className="bench-job__action">
        {job.status === 'queued' ? (
          <Button size="sm" variant="ghost" busy={busy} onClick={onCancel}>
            Cancel
          </Button>
        ) : null}
      </div>
    </li>
  );
}
